import { Credentials } from '@core/entities';
import { AlertType, SectionType } from '@core/enums';
import { AlertStateType } from '@core/types';

/**
 * List of local storage keys
 */
export enum LocalStorageKeys {
  TOKEN = 'token',
  REFRESH_TOKEN = 'refreshToken',
  USER_ID = 'userId',
  TEAM_ID = 'teamId',
  MEMBER_ID = 'memberId',
  DATABASE_ID = 'databaseId',
  CREDENTIALS = 'credentials',
  ALLOW_ADVERTISMENT = 'allowAdvertisement',
  KEYCLOAK_ID = 'id',
  PUSH_NOTIFICATION_SEEN = 'pushNotificationSeen',
  HEADLINE_OPTION = 'headlineOption',
  THEME = 'theme',
  ROLES = 'roles',
  ALERT_MESSAGES_STATE = 'alertMessagesState',
}

// const prefix = '@tineon:';
// const prodEnv = environment.production;
// const secretKey = 'a-quick-brown-fox-jumps-over-the-lazy-dog';
// const secretKeyName = '@entry';
// const secretKeyValue = 'xx1029384756';

export const GetCredentials = () => getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS) as Credentials;

export const SetCredentials = (credentials: Credentials) => saveDataToLocalStorage(LocalStorageKeys.CREDENTIALS, credentials);

export const GetToken = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.TOKEN);
  return credentials[LocalStorageKeys.TOKEN];
};

export const GetRefreshToken = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.REFRESH_TOKEN);
  return credentials[LocalStorageKeys.REFRESH_TOKEN];
};

export const GetUserId = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.USER_ID);
  return credentials[LocalStorageKeys.USER_ID];
};

export const GetKeycloakId = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.KEYCLOAK_ID);
  return credentials[LocalStorageKeys.KEYCLOAK_ID];
};

export const GetMemberId = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.MEMBER_ID);
  return credentials[LocalStorageKeys.MEMBER_ID];
};

export const GetTeamId = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.TEAM_ID);
  return credentials[LocalStorageKeys.TEAM_ID];
};

export const GetDatabaseId = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.DATABASE_ID);
  return credentials[LocalStorageKeys.DATABASE_ID];
};

export const GetAllowAdvertisement = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS) as Credentials;
  if (credentials && credentials?.allowAdvertisement) {
    return credentials[LocalStorageKeys.ALLOW_ADVERTISMENT];
  }
  return 0;
};

export const GetRoles = () => {
  const credentials = getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  checkField(credentials, LocalStorageKeys.ROLES);
  return credentials[LocalStorageKeys.ROLES];
};

export const GetTheme = () => getDataFromLocalStorage(LocalStorageKeys.THEME) || 'light';

export const SetPushNotificationSeen = () => localStorage.setItem(LocalStorageKeys.PUSH_NOTIFICATION_SEEN, 'true');

export const GetPushNotificationSeen = () => localStorage.getItem(LocalStorageKeys.PUSH_NOTIFICATION_SEEN) === 'false';

export const ClearPushNotificationSeen = () => localStorage.removeItem(LocalStorageKeys.PUSH_NOTIFICATION_SEEN);

export const SetHeadlineOption = (headlineOption: 0 | 1) => {
  const credentials = GetCredentials();
  credentials[LocalStorageKeys.HEADLINE_OPTION] = headlineOption || 0;
  SetCredentials(credentials);
};

export const GetHeadlineOption = () => {
  const credentials = JSON.parse(getDataFromLocalStorageWithoutRedirect(LocalStorageKeys.CREDENTIALS)) as Credentials;
  if (credentials && credentials?.headlineOption) {
    return credentials[LocalStorageKeys.HEADLINE_OPTION];
  }
  return 0;
};

export const ClearStorage = () => clearStorage();

export const RemoveAuthData = () => {
  for (const key in LocalStorageKeys) {
    removeDataFromLocalStorage(key);
  }
};

// Alert state management functions

/**
 * Generates a section-user key for the alert storage
 * This ensures one record per section/user combination
 */
export const GetSectionUserKey = (userId: number, sectionType: SectionType, entityId?: number): string => {
  return `${userId}_${sectionType}${entityId ? '_' + entityId : ''}`;
};
/**
 * Get all alert states from storage
 */
export const GetAllAlertStates = (): Record<string, AlertStateType> => {
  try {
    const stored = getDataFromLocalStorageWithoutRedirect(LocalStorageKeys.ALERT_MESSAGES_STATE);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error retrieving alert states:', error);
    return {};
  }
};

/**
 * Save all alert states to storage
 */
export const SaveAllAlertStates = (states: Record<string, AlertStateType>): void => {
  try {
    saveDataToLocalStorage(LocalStorageKeys.ALERT_MESSAGES_STATE, states);
  } catch (error) {
    console.error('Error saving alert states:', error);
  }
};

/**
 * Save a single alert state
 */
export const SaveAlertState = (userId: number, sectionType: SectionType, alertType: AlertType, alertState: AlertStateType, entityId?: number): void => {
  try {
    const states = GetAllAlertStates();
    const key = GetSectionUserKey(userId, sectionType, entityId);
    states[key] = alertState;
    saveDataToLocalStorage(LocalStorageKeys.ALERT_MESSAGES_STATE, states);
  } catch (error) {
    console.error('Error saving alert state:', error);
  }
};

/**
 * Get a specific alert state
 */
export const GetAlertState = (userId: number, sectionType: SectionType, entityId?: number): AlertStateType | null => {
  try {
    const states = GetAllAlertStates();
    const key = GetSectionUserKey(userId, sectionType, entityId);
    return states[key] || null;
  } catch (error) {
    console.error('Error retrieving alert state:', error);
    return null;
  }
};

/**
 * Clear a specific alert state
 */
export const ClearAlertState = (userId: number, sectionType: SectionType, entityId?: number): void => {
  try {
    const states = GetAllAlertStates();
    const key = GetSectionUserKey(userId, sectionType, entityId);
    if (states[key]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete states[key];
      saveDataToLocalStorage(LocalStorageKeys.ALERT_MESSAGES_STATE, states);
    }
  } catch (error) {
    console.error('Error clearing alert state:', error);
  }
};

/**
 * Clean up old alert states (older than 30 days)
 */
export const CleanupOldAlertStates = (): void => {
  try {
    const states = GetAllAlertStates();
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    let hasChanges = false;

    Object.keys(states).forEach((key) => {
      if (now - states[key].timestamp > THIRTY_DAYS) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete states[key];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      saveDataToLocalStorage(LocalStorageKeys.ALERT_MESSAGES_STATE, states);
    }
  } catch (error) {
    console.error('Error cleaning up old alert states:', error);
  }
};

const saveDataToLocalStorage = (key: string, value: any) => {
  // if (prodEnv) {
  //   key = btoa(prefix + key.toString()); // Encode key as base64
  //   key = xorWithSecretKey(key, secretKey); // XOR the key with the secret key
  //   value = btoa(JSON.stringify(value)); // Encode value as base64
  //   value = xorWithSecretKey(value, secretKey); // XOR the value with the secret key
  //   localStorage.setItem(secretKeyName, secretKeyValue);
  // }
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Retrieves data from localStorage and logs out if the key does not exist.
 */
const getDataFromLocalStorage = (key: string) => {
  // const isSecretKey = localStorage.getItem(secretKeyName) === secretKeyValue;
  // if (prodEnv && isSecretKey) {
  //   key = btoa(prefix + key.toString()); // Encode key as base64
  //   key = xorWithSecretKey(key, secretKey); // XOR the key with the secret key
  //   const data = localStorage.getItem(key);
  //   if (!data) window.location.href = '/logout';
  //   const decodedValue = xorWithSecretKey(data, secretKey); // XOR the value with the secret key
  //   return JSON.parse(atob(decodedValue)); // Decode base64 value
  // }

  const data = localStorage.getItem(key);
  if (!data) {
    window.location.href = '/logout';
  }
  return JSON.parse(data);
};

/**
 * Helper to check if a particular field exists in an object.
 * If it does not exist, logs out.
 */
const checkField = (obj: Record<string, any>, fieldKey: string) => {
  if (!obj || obj[fieldKey] === null || obj[fieldKey] === undefined) {
    window.location.href = '/logout';
  }
};

// const xorWithSecretKey = (text: string, secret: string): string => {
//   let result = '';
//   for (let i = 0; i < text.length; i++) {
//     result += String.fromCharCode(text.charCodeAt(i) ^ secret.charCodeAt(i % secret.length));
//   }
//   return result;
// };

const removeDataFromLocalStorage = (key: any) => {
  // if (prodEnv) {
  //   key = btoa(prefix + key.toString()); // Encode key as base64
  //   key = xorWithSecretKey(key, secretKey); // XOR the key with the secret key
  // }
  localStorage.removeItem(key);
};

const getDataFromLocalStorageWithoutRedirect = (key: LocalStorageKeys) => {
  return localStorage.getItem(key);
};

const clearStorage = () => localStorage.clear();
